import { Truck, Check } from "lucide-react";
import { FREE_SHIPPING_THRESHOLD } from "@/lib/shipping";

interface FreeShippingProgressProps {
  currentTotal: number;
  threshold?: number;
  currencyCode?: string;
}

export const FreeShippingProgress = ({ 
  currentTotal, 
  // The supplier's real threshold. A $99 default here told drawer shoppers
  // they had free shipping on $99–$119 carts that checkout charged $10.
  threshold = FREE_SHIPPING_THRESHOLD,
  currencyCode = "USD" 
}: FreeShippingProgressProps) => {
  const remaining = threshold - currentTotal;
  const progress = Math.min((currentTotal / threshold) * 100, 100);
  const qualified = currentTotal >= threshold;

  // To the cent when the gap has cents: rounded to "$66" for a $53.99 cart, it
  // named an amount that still left the order a cent short of free shipping.
  const formatAmount = (amount: number) => {
    const cents = Math.ceil(Math.round(amount * 10000) / 100);
    const digits = cents % 100 === 0 ? 0 : 2;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(cents / 100);
  };

  return (
    // Tighter on a short screen, where the drawer's cart lines need the room.
    <div className="rounded-2xl bg-sand/50 p-3 [@media(min-height:800px)]:p-4 [@media(min-height:800px)]:mb-4">
      <div className="flex items-center gap-2 mb-2">
        {qualified ? (
          <Check className="h-4 w-4 text-primary flex-shrink-0" />
        ) : (
          <Truck className="h-4 w-4 text-primary flex-shrink-0" />
        )}
        <p className="text-[13px] font-medium text-foreground">
          {qualified ? (
            "You've unlocked free U.S. shipping!"
          ) : (
            <>Add <span className="text-primary font-bold">{formatAmount(remaining)}</span> more for free U.S. shipping</>
          )}
        </p>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div 
          className="h-full transition-all duration-500 rounded-full bg-primary"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};
